import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../App';
import { Bell, Check, CheckCheck, Trash2, BookOpen, Video, GraduationCap, School, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
  school_content: School,
  new_content: BookOpen,
  live_class_reminder: Video,
  assignment: GraduationCap,
  broadcast: Bell,
  reminder: Bell,
};

const TYPE_COLORS = {
  school_content: 'text-indigo-600 bg-indigo-50',
  new_content: 'text-blue-600 bg-blue-50',
  live_class_reminder: 'text-red-600 bg-red-50',
  assignment: 'text-purple-600 bg-purple-50',
  broadcast: 'text-amber-600 bg-amber-50',
  reminder: 'text-teal-600 bg-teal-50',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function NotificationBell() {
  const { user, token } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  // Track last seen notification id so we only toast genuinely-new ones
  // (not the entire backlog when the bell mounts). Persisted across
  // remounts via sessionStorage so refreshes don't re-pop old toasts.
  const lastSeenIdRef = useRef(null);
  const initialisedRef = useRef(false);

  const fetchCount = useCallback(async () => {
    if (!token) return;
    try {
      // 1) Refresh badge count
      const res = await fetch(`${API}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count || 0);
      }
      // 2) Pull latest 5 unread notifications and pop a sonner toast for
      // each NEW one (newer than `lastSeenIdRef`). On the first run we
      // simply record the most recent id as the cursor — no toasts so we
      // don't blast the user with their backlog on refresh.
      const latest = await fetch(`${API}/api/notifications?limit=5&unread_only=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (latest.ok) {
        const items = await latest.json();
        if (Array.isArray(items) && items.length) {
          const newest = items[0];
          if (!initialisedRef.current) {
            // Seed cursor from sessionStorage if available so a refresh
            // continues from the same point in the stream.
            const stored = sessionStorage.getItem('notif_cursor_id');
            lastSeenIdRef.current = stored || newest.id;
            initialisedRef.current = true;
          } else if (newest.id !== lastSeenIdRef.current) {
            // Iterate from oldest-fresh → newest so toasts show in order.
            const cursor = lastSeenIdRef.current;
            const fresh = [];
            for (const n of items) {
              if (n.id === cursor) break;
              fresh.push(n);
            }
            fresh.reverse().forEach(n => {
              const isChat = n.type === 'chat_message';
              const chatId = n.data?.chat_id;
              toast(n.title || 'New notification', {
                description: n.message,
                duration: 5000,
                action: isChat && chatId
                  ? { label: 'Open', onClick: () => { window.location.href = `/chat/${chatId}`; } }
                  : undefined,
              });
            });
            lastSeenIdRef.current = newest.id;
            sessionStorage.setItem('notif_cursor_id', newest.id);
          }
        }
      }
    } catch {}
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data || []);
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchCount();
    // Tighter poll interval (was 30s) so chat messages feel near-real-time.
    const interval = setInterval(fetchCount, 12000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markRead = async (id) => {
    await fetch(`${API}/api/notifications/${id}/read`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    });
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(c => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/notifications/read-all`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    });
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const clearAll = async () => {
    await fetch(`${API}/api/notifications`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setNotifications([]);
    setUnreadCount(0);
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref} data-testid="notification-bell-container">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-slate-600 hover:text-indigo-600 transition-colors rounded-lg hover:bg-slate-100"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h3 className="text-sm font-bold text-slate-800">Notifications</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="p-1.5 text-xs text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center gap-1" data-testid="mark-all-read-btn" title="Mark all read">
                  <CheckCheck className="w-3.5 h-3.5" />
                </button>
              )}
              {notifications.length > 0 && (
                <button onClick={clearAll} className="p-1.5 text-xs text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-testid="clear-all-btn" title="Clear all">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Notifications list */}
          <div className="max-h-96 overflow-y-auto" data-testid="notification-list">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-10 px-4" data-testid="no-notifications">
                <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const IconComp = TYPE_ICONS[n.type] || Bell;
                const colorClass = TYPE_COLORS[n.type] || 'text-slate-600 bg-slate-50';
                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${!n.is_read ? 'bg-indigo-50/40' : ''}`}
                    onClick={() => !n.is_read && markRead(n.id)}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <IconComp className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!n.is_read ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>{n.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.is_read && (
                      <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-2" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
